import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import client from '../../api/client';
import FilterBar from '../../components/FilterBar';
import { dedupeLocations } from '../../utils/locations';

// ── helpers ─────────────────────────────────────────────────────────────────

import TlOverview from './TlOverview';
function fmtInr(n) {
  if (!n && n !== 0) return '—';
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtMins(m) {
  if (!m) return '0 min';
  if (m < 60) return `${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return min ? `${h}h ${min}m` : `${h}h`;
}

function uptime4(n) {
  return Number(n).toFixed(4) + '%';
}

const QUARTER_MONTHS = { 1: 'Apr – Jun', 2: 'Jul – Sep', 3: 'Oct – Dec', 4: 'Jan – Mar' };
const QUARTER_TO_MONTHS = {
  1: [{ n: 4, label: 'Apr' }, { n: 5, label: 'May' }, { n: 6, label: 'Jun' }],
  2: [{ n: 7, label: 'Jul' }, { n: 8, label: 'Aug' }, { n: 9, label: 'Sep' }],
  3: [{ n: 10, label: 'Oct' }, { n: 11, label: 'Nov' }, { n: 12, label: 'Dec' }],
  4: [{ n: 1, label: 'Jan' }, { n: 2, label: 'Feb' }, { n: 3, label: 'Mar' }],
};

const STATUS_COLORS = {
  GREEN: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400', label: 'SLA Met' },
  AMBER: { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400',   label: 'At Risk' },
  RED:   { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500',     label: 'Breach'  },
};

const STATUS_RANK = { GREEN: 0, AMBER: 1, RED: 2 };

const TICKET_STATUSES = [
  'Open','Ticket Assignment','Troubleshooting','Fault Confirmation','On Hold','RMA','Replacement',
  'Configuration and Installation','Maintenance','Asset Review','Resolved','Recovery','Closed',
];

const STATUS_BAR_COLOR = [
  'bg-brand-500','bg-purple-500','bg-amber-400','bg-yellow-500','bg-orange-400','bg-red-500','bg-pink-500',
  'bg-sky-500','bg-indigo-500','bg-teal-500','bg-emerald-500','bg-lime-500','bg-gray-300',
];

// ── sub-components ───────────────────────────────────────────────────────────

function SlaBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.GREEN;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

function UptimeBar({ actual, target }) {
  const pct = Math.min(100, Math.max(0, actual));
  const color = actual >= target ? 'bg-emerald-400' : actual >= target - 0.5 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="relative w-full h-2 bg-gray-100 rounded-full overflow-visible">
      <div className={`absolute left-0 top-0 h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      <div
        className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-gray-400 rounded-full"
        style={{ left: `${Math.min(100, target)}%` }}
        title={`Target: ${target}%`}
      />
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

function ExplainBtn({ onClick }) {
  return (
    <button type="button" onClick={onClick} title="How is this calculated?"
      className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-50 text-brand-500 text-[11px] font-bold hover:bg-brand-100 align-middle normal-case">i</button>
  );
}

function fmtMin(m) {
  return `${Number(m || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })} min`;
}

// A ticket contributes to the SLA only if it was counted (marked "Mark as downtime") AND added minutes inside working hours.
const contributing = tickets => (tickets || []).filter(t => t.counted && Number(t.minutes) > 0);

function ExplainTickets({ tickets }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs mt-2">
        <thead>
          <tr className="text-left text-gray-400 uppercase">
            <th className="py-1 pr-2">Ticket</th><th className="pr-2">Status</th><th className="pr-2">Reported</th>
            <th className="pr-2">Resolved</th><th className="pr-2">On hold (excluded)</th><th className="pr-2">Minutes counted</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map(t => (
            <tr key={t.id} className={`border-t ${t.counted ? '' : 'text-gray-400'}`}>
              <td className="py-1 pr-2 font-semibold">{t.ticket_number}</td>
              <td className="pr-2">{t.status}</td>
              <td className="pr-2">{t.reported || '—'}</td>
              <td className="pr-2">{t.ongoing ? 'Still open (counted till now)' : (t.resolved || '—')}</td>
              <td className="pr-2">{t.counted ? fmtMin(t.hold_minutes) : '—'}</td>
              <td className="pr-2">{t.counted ? <b className="text-red-600">{fmtMin(t.minutes)}</b> : <span>Not counted: {t.reason}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExplainRooms({ rooms, open, setOpen, showPenalty }) {
  const cols = showPenalty ? 6 : 5;
  // Only the tickets that add counted downtime minutes to the SLA are listed, and a room with none is not listed.
  // A room that still has downtime but whose tickets would all be filtered out keeps its full list, so nothing that
  // is charged can disappear.
  const rows = rooms
    .map(r => {
      const counted = contributing(r.tickets);
      return { ...r, tickets: counted.length || !(r.downtime_minutes > 0) ? counted : r.tickets };
    })
    .filter(r => r.tickets.length > 0 || r.downtime_minutes > 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-400 uppercase border-b">
            <th className="py-2 pr-2">Location / Room</th><th className="pr-2">Downtime (d)</th>
            <th className="pr-2">Uptime</th><th className="pr-2">Target</th>
            {showPenalty && <th className="pr-2">Deduction</th>}<th />
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <React.Fragment key={r.id}>
              <tr className="border-b">
                <td className="py-2 pr-2"><b>{r.location}</b> · {r.room_name} <span className="text-gray-400">{r.room_code}</span></td>
                <td className="pr-2">{fmtMin(r.downtime_minutes)}</td>
                <td className="pr-2">{Number(r.uptime_pct).toFixed(4)}%</td>
                <td className="pr-2">{r.target_uptime_pct}%</td>
                {showPenalty && <td className="pr-2 font-semibold text-red-600">{fmtInr(r.deduction)}</td>}
                <td className="text-right whitespace-nowrap">
                  <button type="button" className="text-brand-500 underline"
                    onClick={() => setOpen(o => ({ ...o, [r.id]: !o[r.id] }))}>
                    {open[r.id] ? 'Hide' : 'Tickets'} ({r.tickets.length})
                  </button>
                </td>
              </tr>
              {open[r.id] && (
                <tr>
                  <td colSpan={cols} className="bg-gray-50 px-3 pb-3">
                    <p className="text-gray-500 mt-2">
                      Uptime = ({fmtMin(r.total_minutes)} − {fmtMin(r.downtime_minutes)}) ÷ {fmtMin(r.total_minutes)} × 100 = <b>{Number(r.uptime_pct).toFixed(4)}%</b>
                      {r.overlap_minutes > 0 && <> · {fmtMin(r.overlap_minutes)} of overlapping tickets counted only once</>}
                    </p>
                    {showPenalty && <p className="text-gray-500 mt-1">Deduction: {r.formula}</p>}
                    <ExplainTickets tickets={r.tickets} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExplainModal({ state, onClose }) {
  const { kind, loading, data, error } = state;
  const [open, setOpen] = useState({});
  const title = kind === 'PENALTY' ? 'How the penalty is calculated' : `How Class ${kind} uptime is calculated`;
  const p = data?.period;
  const c = data?.class_summary;
  const t = data?.totals;
  const re = data?.re;
  const slaRooms = (data?.rooms || []).filter(r => contributing(r.tickets).length > 0);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-brand-600">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="p-6 overflow-y-auto space-y-5 text-sm text-gray-700">
          {loading && <p className="text-gray-400">Calculating…</p>}
          {error && <p className="text-red-600">{error}</p>}
          {data && (
            <>
              <div className="bg-brand-50 rounded-xl p-4 space-y-1">
                <p><b>Period:</b> {p.label} ({p.start} to {p.end}, end date not included)</p>
                <p><b>Total minutes per room:</b> {p.working_days} working days (Mon–Fri, Sat/Sun excluded) × {p.hours_per_day} h ({p.service_window}) × 60 = <b>{fmtMin(p.total_minutes)}</b></p>
                <p><b>Tickets counted:</b> only tickets with “Mark as downtime” ticked. Time On Hold is excluded, overlapping tickets in the same room count once, and only minutes inside {p.service_window} on working days count.</p>
              </div>

              {kind !== 'PENALTY' && c && (
                <>
                  <div className="rounded-xl border p-4 space-y-1">
                    <p className="font-semibold text-brand-600">Class {c.cls} result</p>
                    <p>Rooms: <b>{c.rooms}</b> · Possible minutes = {c.rooms} × {fmtMin(p.total_minutes)} = <b>{fmtMin(c.possible_minutes)}</b></p>
                    <p>Total downtime of all Class {c.cls} rooms (Σd) = <b>{fmtMin(c.downtime_minutes)}</b></p>
                    <p className="text-base">Uptime = ({fmtMin(c.possible_minutes)} − {fmtMin(c.downtime_minutes)}) ÷ {fmtMin(c.possible_minutes)} × 100 = <b className="text-brand-600">{Number(c.uptime_pct).toFixed(2)}%</b> <span className="text-gray-400">(target {c.target}%)</span></p>
                    {!data.matches_dashboard && <p className="text-amber-600">Note: the ticket breakdown differs slightly from the dashboard figure. Please report this.</p>}
                  </div>
                  <p className="text-gray-500">
                    {c.rooms - slaRooms.length} room(s) had no downtime ticket that counts toward the SLA in this period (100% uptime each).
                    {slaRooms.length > 0 && ' Rooms with downtime tickets that count toward the SLA:'}
                  </p>
                  {slaRooms.length ? <ExplainRooms rooms={slaRooms} open={open} setOpen={setOpen} showPenalty={false} />
                    : <p className="text-emerald-600">No downtime ticket counted toward Class {c.cls} uptime in this period.</p>}
                </>
              )}

              {kind === 'PENALTY' && t && (
                <>
                  <div className="rounded-xl border p-4 space-y-1">
                    <p className="font-semibold text-brand-600">Rules for each room (unit)</p>
                    <p>• Uptime at or above target (Class A 99%, Class B 98.5%, LED video wall 99%): <b>no deduction</b></p>
                    <p>• Uptime below target: deduction = d × r</p>
                    <p>• Uptime below 80%: deduction = d × r × 2</p>
                    <p className="text-gray-500">d = the room's downtime minutes; r = the room's quarterly rental ÷ the quarter's total minutes (rental per minute).</p>
                  </div>
                  <div className="rounded-xl border p-4 space-y-1">
                    <p className="font-semibold text-brand-600">Total</p>
                    <p>Service penalty (sum of room deductions) = <b>{fmtInr(t.service_penalty)}</b> · {t.penalised_rooms} room(s)</p>
                    <p>RE penalty = {re.available
                      ? <>{Number(re.absent_days)} absent day(s) × ₹{re.per_day} = <b>{fmtInr(re.penalty)}</b></>
                      : <span className="text-amber-600">Zoho is not reachable right now ({re.error})</span>}</p>
                    <p className="text-base">Total penalty = {fmtInr(t.service_penalty)} + {fmtInr(re.penalty || 0)} = <b className="text-red-600">{fmtInr(t.service_penalty + (re.penalty || 0))}</b></p>
                  </div>
                  {data.rooms.length
                    ? <ExplainRooms rooms={data.rooms} open={open} setOpen={setOpen} showPenalty />
                    : <p className="text-emerald-600">No room is below its target in this period, so there is no service penalty.</p>}
                  {re.available && (
                    <div>
                      <p className="font-semibold text-brand-600 mb-2">RE attendance from Zoho People: ₹{re.per_day} per absent day, per RE</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead><tr className="text-left text-gray-400 uppercase border-b">
                            <th className="py-1 pr-2">RE</th><th className="pr-2">Present days</th><th className="pr-2">Absent days</th><th className="pr-2">Penalty</th>
                          </tr></thead>
                          <tbody>
                            {re.rows.map(r => (
                              <tr key={r.id} className="border-b">
                                <td className="py-1 pr-2">{r.name}{!r.matched && <span className="text-amber-600"> (not linked to Zoho)</span>}</td>
                                <td className="pr-2">{r.present_days ?? '—'}</td>
                                <td className="pr-2">{Number(r.absent_days)}</td>
                                <td className={`pr-2 ${r.penalty > 0 ? 'text-red-600 font-semibold' : ''}`}>{fmtInr(r.penalty)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-gray-400 text-xs mt-1">Only days Zoho marks as Absent count. Leave and half days are not counted as absent.</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  useSelector(s => s.auth.user);

  const today = new Date();
  const m = today.getMonth() + 1;
  const defaultFyYear = m >= 4 ? today.getFullYear() : today.getFullYear() - 1;
  const defaultQ = m >= 4 && m <= 6 ? 1 : m >= 7 && m <= 9 ? 2 : m >= 10 ? 3 : 4;

  const [fyYear, setFyYear]   = useState(defaultFyYear);
  const [quarter, setQuarter] = useState(defaultQ);
  const [month, setMonth]     = useState(null);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customApplied, setCustomApplied] = useState(false);
  const [data, setData]       = useState(null);
  const [allRooms, setAllRooms] = useState([]);
  const [locationAttendance, setLocationAttendance] = useState({});
  const [loading, setLoading]   = useState(true);

  // Filters
  const [locationFilter,    setLocationFilter]    = useState('');
  const [subLocationFilter, setSubLocationFilter] = useState('');
  const [roomNameFilter,    setRoomNameFilter]    = useState('');
  const [slaFilter,         setSlaFilter]         = useState('ALL');
  const [classFilter,       setClassFilter]       = useState('ALL');
  const [explain, setExplain] = useState(null);
  const openExplain = async (kind) => {
    setExplain({ kind, loading: true });
    try {
      const rangeParam = (customApplied && customStart && customEnd)
        ? `&start=${customStart}&end=${customEnd}`
        : (month ? `&month=${month}` : '');
      const r = await client.get(`/reports/sla-dashboard/explain/?year=${fyYear}&quarter=${quarter}&cls=${kind}${rangeParam}`);
      setExplain({ kind, loading: false, data: r.data });
    } catch (e) {
      setExplain({ kind, loading: false, error: e.response?.data?.detail || 'Could not load the calculation.' });
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rangeParam = (customApplied && customStart && customEnd)
        ? `&start=${customStart}&end=${customEnd}`
        : (month ? `&month=${month}` : '');
      const [dashRes, roomsRes] = await Promise.all([
        client.get(`/reports/sla-dashboard/?year=${fyYear}&quarter=${quarter}${rangeParam}`),
        client.get(`/reports/sla-rooms/?year=${fyYear}&quarter=${quarter}${rangeParam}`),
      ]);
      setData(dashRes.data);
      setAllRooms(roomsRes.data.rooms || []);
      setLocationAttendance(roomsRes.data.location_attendance || {});
    } catch { /* keep stale */ }
    finally { setLoading(false); }
  }, [fyYear, quarter, month, customApplied, customStart, customEnd]);

  useEffect(() => { load(); }, [load]);

  const [exportingSla, setExportingSla] = useState(false);
  const handleExportSla = async () => {
    setExportingSla(true);
    try {
      const rangeParam = (customApplied && customStart && customEnd)
        ? `&start=${customStart}&end=${customEnd}`
        : (month ? `&month=${month}` : '');
      const resp = await client.get(`/reports/sla-dashboard/export/?year=${fyYear}&quarter=${quarter}${rangeParam}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sla_dashboard_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to export SLA dashboard.');
    } finally {
      setExportingSla(false);
    }
  };

  // Reset filters on quarter change
  useEffect(() => {
    setLocationFilter('');
    setSubLocationFilter('');
    setRoomNameFilter('');
    setSlaFilter('ALL');
    setMonth(null);
  }, [fyYear, quarter]);

  const q        = data?.quarter || {};
  const pipeline = data?.ticket_pipeline || {};
  const globalS  = data?.summary || {};
  const pipelineTotal = Object.values(pipeline).reduce((a, b) => a + b, 0);
  const yearOptions   = [defaultFyYear, defaultFyYear - 1, defaultFyYear - 2];

  // ── Cascading dropdown options ─────────────────────────────────────────────
  const locationOptions = useMemo(
    () => [...new Set(allRooms.map(r => r.location_name).filter(Boolean))].sort(),
    [allRooms]
  );
  const subLocationOptions = useMemo(() => [...new Set(
    allRooms
      .filter(r => !locationFilter || r.location_name === locationFilter)
      .map(r => r.building_name).filter(Boolean)
  )].sort(), [allRooms, locationFilter]);
  const roomNameOptions = useMemo(() => [...new Set(
    allRooms
      .filter(r => {
        if (locationFilter    && r.location_name !== locationFilter)    return false;
        if (subLocationFilter && r.building_name  !== subLocationFilter) return false;
        return true;
      })
      .map(r => r.room_name).filter(Boolean)
  )].sort(), [allRooms, locationFilter, subLocationFilter]);

  // ── Filtered rooms (all filters applied) ──────────────────────────────────
  const filteredRooms = useMemo(() => allRooms.filter(r => {
    if (slaFilter !== 'ALL'  && r.sla_status    !== slaFilter)         return false;
    if (classFilter !== 'ALL' && r.sla_class    !== classFilter)       return false;
    if (locationFilter       && r.location_name !== locationFilter)    return false;
    if (subLocationFilter    && r.building_name  !== subLocationFilter) return false;
    if (roomNameFilter       && r.room_name      !== roomNameFilter)    return false;
    return true;
  }), [allRooms, slaFilter, classFilter, locationFilter, subLocationFilter, roomNameFilter]);

  // ── Filtered rooms without SLA pill (for pill counts) ─────────────────────
  const preFilteredRooms = useMemo(() => allRooms.filter(r => {
    if (locationFilter    && r.location_name !== locationFilter)    return false;
    if (subLocationFilter && r.building_name  !== subLocationFilter) return false;
    if (roomNameFilter    && r.room_name      !== roomNameFilter)    return false;
    return true;
  }), [allRooms, locationFilter, subLocationFilter, roomNameFilter]);

  const hasFilters = !!(locationFilter || subLocationFilter || roomNameFilter);

  // ── Summary stats derived from filteredRooms ──────────────────────────────
  const derivedStats = useMemo(() => {
    const rooms = filteredRooms;
    if (!rooms.length) return { fleetUptime: 100, ticketPenalty: 0, roomRental: 0, activeTickets: 0, attAbsent: 0, attPenalty: null, green: 0, amber: 0, red: 0, total: 0 };

    const fleetUptime   = rooms.reduce((s, r) => s + r.uptime_pct, 0) / rooms.length;
    const ticketPenalty = rooms.reduce((s, r) => s + (r.penalty_amount || 0), 0);
    const roomRental    = rooms.reduce((s, r) => s + (r.quarterly_rental_total || r.quarterly_rental_calculated || 0), 0);
    const activeTickets = rooms.reduce((s, r) => s + (r.open_cases || 0), 0);

    // Per-location worst SLA status + collect unique location IDs
    const locStatus = {};
    const locIds = new Set();
    for (const r of rooms) {
      locIds.add(r.location_id);
      const cur = locStatus[r.location_name];
      if (!cur || STATUS_RANK[r.sla_status] > STATUS_RANK[cur]) {
        locStatus[r.location_name] = r.sla_status;
      }
    }

    // Attendance penalty — sum across unique filtered locations (null = use global)
    let attAbsent = 0;
    let attPenalty = null;
    if (hasFilters) {
      attPenalty = 0;
      for (const lid of locIds) {
        const a = locationAttendance[lid];
        if (a) { attAbsent += a.att_absent; attPenalty += a.att_penalty; }
      }
    }

    const statuses = Object.values(locStatus);
    return {
      fleetUptime, ticketPenalty, roomRental, activeTickets,
      attAbsent, attPenalty,
      green: statuses.filter(s => s === 'GREEN').length,
      amber: statuses.filter(s => s === 'AMBER').length,
      red:   statuses.filter(s => s === 'RED').length,
      total: statuses.length,
    };
  }, [filteredRooms, locationAttendance, hasFilters]);

  const isFiltered = hasFilters || slaFilter !== 'ALL' || classFilter !== 'ALL';

  return (
    <div className="p-6 space-y-6">

      {/* ── Header + Quarter selector ── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-500">SLA Dashboard</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            {q.label ? `${q.label} · ${q.start} to ${q.end}` : 'Loading…'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={fyYear} onChange={e => setFyYear(Number(e.target.value))} className="input-field text-sm py-1.5 w-32">
            {yearOptions.map(y => <option key={y} value={y}>FY {y}-{String(y + 1).slice(2)}</option>)}
          </select>
          <div className="flex rounded-xl overflow-hidden border border-brand-200 bg-white shadow-sm">
            {[1, 2, 3, 4].map(qn => (
              <button key={qn} onClick={() => { setQuarter(qn); setMonth(null); setCustomApplied(false); }}
                className={`px-3 py-1.5 text-sm font-semibold transition-colors ${quarter === qn && !month && !customApplied ? 'bg-brand-500 text-white' : 'text-brand-400 hover:bg-brand-50'}`}>
                Q{qn}
                <span className="hidden sm:inline text-xs font-normal ml-1 opacity-70">{QUARTER_MONTHS[qn]}</span>
              </button>
            ))}
          </div>
          {/* Month-wise filter — narrows the current quarter down to one calendar month */}
          <div className="flex rounded-xl overflow-hidden border border-brand-200 bg-white shadow-sm">
            {QUARTER_TO_MONTHS[quarter].map(({ n, label: mLabel }) => (
              <button key={n} onClick={() => { setMonth(month === n ? null : n); setCustomApplied(false); }}
                className={`px-3 py-1.5 text-sm font-semibold transition-colors ${month === n && !customApplied ? 'bg-magenta-500 text-white' : 'text-brand-400 hover:bg-brand-50'}`}
                title={month === n ? 'Click again to show the full quarter' : `Show only ${mLabel}`}>
                {mLabel}
              </button>
            ))}
          </div>
          {/* Custom date range — overrides Quarter/Month when applied */}
          <div className="flex items-center gap-1 rounded-xl border border-brand-200 bg-white shadow-sm px-2 py-1">
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="text-sm px-1 py-0.5 border-none focus:outline-none" />
            <span className="text-gray-300 text-sm">–</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="text-sm px-1 py-0.5 border-none focus:outline-none" />
            <button onClick={() => { if (customStart && customEnd) setCustomApplied(true); }}
              disabled={!customStart || !customEnd}
              className={`ml-1 px-2 py-1 rounded-lg text-xs font-semibold transition-colors disabled:opacity-40 ${customApplied ? 'bg-emerald-500 text-white' : 'bg-brand-500 text-white hover:bg-brand-600'}`}>
              {customApplied ? '✓ Applied' : 'Apply'}
            </button>
          </div>
          <button onClick={load} className="p-2 rounded-xl border border-brand-200 text-brand-400 hover:bg-brand-50 transition-colors" title="Refresh">
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button onClick={handleExportSla} disabled={exportingSla}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-brand-600 bg-brand-50 border border-brand-200 rounded-xl hover:bg-brand-100 transition-colors disabled:opacity-50">
            {exportingSla
              ? <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            }
            Download Excel
          </button>
        </div>
      </div>

      {/* ── Filters (top-level) ── */}
      <FilterBar
        hasActiveFilters={hasFilters}
        onResetAll={() => { setLocationFilter(''); setSubLocationFilter(''); setRoomNameFilter(''); }}
      >
        <FilterBar.Item value={locationFilter} onClear={() => { setLocationFilter(''); setSubLocationFilter(''); setRoomNameFilter(''); }}>
          <select value={locationFilter} onChange={e => { setLocationFilter(e.target.value); setSubLocationFilter(''); setRoomNameFilter(''); }} className="input-field w-auto">
            <option value="">All Locations</option>
            {locationOptions.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </FilterBar.Item>
        {subLocationOptions.length > 0 && (
          <FilterBar.Item value={subLocationFilter} onClear={() => { setSubLocationFilter(''); setRoomNameFilter(''); }}>
            <select value={subLocationFilter} onChange={e => { setSubLocationFilter(e.target.value); setRoomNameFilter(''); }} className="input-field w-auto">
              <option value="">All Sub-Locations</option>
              {subLocationOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FilterBar.Item>
        )}
        {roomNameOptions.length > 0 && (
          <FilterBar.Item value={roomNameFilter} onClear={() => setRoomNameFilter('')}>
            <select value={roomNameFilter} onChange={e => setRoomNameFilter(e.target.value)} className="input-field w-auto">
              <option value="">All Rooms</option>
              {roomNameOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </FilterBar.Item>
        )}
        <FilterBar.Item value={classFilter !== 'ALL' ? classFilter : ''} onClear={() => setClassFilter('ALL')}>
          <select value={classFilter} onChange={e => setClassFilter(e.target.value)} className="input-field w-auto">
            <option value="ALL">All Classes</option>
            {[...new Set(preFilteredRooms.map(r => r.sla_class).filter(Boolean))].sort().map(c => (
              <option key={c} value={c}>Class {c}</option>
            ))}
          </select>
        </FilterBar.Item>
        {isFiltered && (
          <span className="text-xs text-brand-400 font-medium px-1 self-center">
            Showing {filteredRooms.length} of {allRooms.length} rooms
          </span>
        )}
      </FilterBar>

      {loading && !data ? (
        <div className="flex items-center justify-center py-24">
          <svg className="animate-spin w-8 h-8 text-magenta-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
        </div>
      ) : (
        <>

          {/* ── Summary cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {explain && <ExplainModal state={explain} onClose={() => setExplain(null)} />}
            {/* 1. Class A Uptime */}
            <div className={`card p-5 border-l-4 ${(globalS.class_a_uptime_pct ?? 100) >= 99 ? 'border-l-emerald-400' : (globalS.class_a_uptime_pct ?? 100) >= 98 ? 'border-l-amber-400' : 'border-l-red-400'}`}>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Class A Uptime<ExplainBtn onClick={() => openExplain('A')} /></p>
              <p className={`text-3xl font-black ${(globalS.class_a_uptime_pct ?? 100) >= 99 ? 'text-emerald-600' : (globalS.class_a_uptime_pct ?? 100) >= 98 ? 'text-amber-600' : 'text-red-600'}`}>
                {(globalS.class_a_uptime_pct ?? 100).toFixed(2)}%
              </p>
            </div>

            {/* 2. Class B Uptime */}
            <div className={`card p-5 border-l-4 ${(globalS.class_b_uptime_pct ?? 100) >= 98.5 ? 'border-l-emerald-400' : 'border-l-red-400'}`}>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Class B Uptime<ExplainBtn onClick={() => openExplain('B')} /></p>
              <p className={`text-3xl font-black ${(globalS.class_b_uptime_pct ?? 100) >= 98.5 ? 'text-emerald-600' : 'text-red-600'}`}>
                {(globalS.class_b_uptime_pct ?? 100).toFixed(2)}%
              </p>
            </div>

            {/* 3. RE Penalty */}
            <div className={`card p-5 border-l-4 ${(globalS.re_penalty ?? 0) > 0 ? 'border-l-red-400' : 'border-l-emerald-400'}`}>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">RE Penalty<ExplainBtn onClick={() => openExplain('PENALTY')} /></p>
              <p className={`text-3xl font-black ${(globalS.re_penalty ?? 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {fmtInr(globalS.re_penalty ?? 0)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {(globalS.att_absent ?? 0) > 0
                  ? `${globalS.att_absent} absent day${globalS.att_absent > 1 ? 's' : ''} this month`
                  : 'Full attendance this month'}
              </p>
            </div>

            {/* 4. Total Penalty */}
            <div className={`card p-5 border-l-4 ${(globalS.total_penalty ?? 0) > 0 ? 'border-l-red-400' : 'border-l-emerald-400'}`}>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Total Penalty<ExplainBtn onClick={() => openExplain('PENALTY')} /></p>
              <p className={`text-3xl font-black ${(globalS.total_penalty ?? 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {fmtInr(globalS.total_penalty ?? 0)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Service {fmtInr(globalS.sla_penalty ?? 0)} + RE {fmtInr(globalS.re_penalty ?? 0)}
              </p>
            </div>
          </div>

          {/* ── Penalty by unit + Summary sheet ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* Penalty by unit — top locations by total penalty (Service + RE) */}
            <div className="lg:col-span-2 card p-5">
              <h2 className="font-bold text-brand-500 mb-4 text-sm">Penalty by unit</h2>
              {(() => {
                const byUnit = (data?.locations || [])
                  .filter(l => (l.total_penalty_amount ?? 0) > 0)
                  .sort((a, b) => (b.total_penalty_amount ?? 0) - (a.total_penalty_amount ?? 0))
                  .slice(0, 10);
                if (byUnit.length === 0) {
                  return <p className="text-sm text-gray-400 text-center py-8">No penalties this period.</p>;
                }
                const maxTotal = Math.max(...byUnit.map(l => l.total_penalty_amount ?? 0), 1);
                return (
                  <>
                    <div className="space-y-3">
                      {byUnit.map(loc => {
                        const service = loc.penalty_amount ?? 0;
                        const re = loc.re_penalty ?? 0;
                        const total = loc.total_penalty_amount ?? 0;
                        const servicePct = (service / maxTotal) * 100;
                        const rePct = (re / maxTotal) * 100;
                        return (
                          <div key={loc.id} className="flex items-center gap-3">
                            <span className="text-xs text-gray-600 w-32 flex-shrink-0 truncate" title={loc.name}>{loc.name}</span>
                            <div className="flex-1 h-5 bg-gray-50 rounded-full overflow-hidden flex">
                              {servicePct > 0 && <div className="h-full bg-sky-700" style={{ width: `${servicePct}%` }} />}
                              {rePct > 0 && <div className="h-full bg-red-500" style={{ width: `${rePct}%` }} />}
                            </div>
                            <span className="text-xs font-bold text-gray-600 w-20 text-right flex-shrink-0">{fmtInr(total)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-4 pt-3 border-t border-brand-50 text-xs text-gray-500">
                      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-sky-700 inline-block" /> Service</span>
                      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500 inline-block" /> RE</span>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Summary sheet */}
            <div className="card p-5">
              <h2 className="font-bold text-brand-500 mb-4 text-sm">Summary sheet</h2>
              <div className="divide-y divide-brand-50">
                {[
                  ['Class A tickets', globalS.class_a_tickets ?? 0],
                  ['Class B tickets', globalS.class_b_tickets ?? 0],
                  ['Ticket penalty (A / B)', `${fmtInr(globalS.class_a_ticket_penalty ?? 0)} / ${fmtInr(globalS.class_b_ticket_penalty ?? 0)}`],
                  ['RE attendance penalty', fmtInr(globalS.re_penalty ?? 0)],
                  ['At risk (A / B)', `${globalS.class_a_at_risk ?? 0} / ${globalS.class_b_at_risk ?? 0}`],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between py-2.5 text-sm">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-bold text-gray-800">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Team Leader open tickets + Team Leader / Site / RE list */}
          <TlOverview />

          {/* ── Room-wise SLA table ── */}
          <div className="card overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-brand-50 flex-wrap gap-3">
              <div>
                <h2 className="font-bold text-brand-500 text-sm">SLA by Room</h2>
                <p className="text-xs text-gray-400 mt-0.5">{filteredRooms.length} room{filteredRooms.length !== 1 ? 's' : ''} shown</p>
              </div>

              {/* SLA status pills */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { key: 'ALL',   label: `All (${preFilteredRooms.length})`,                                        active: 'bg-brand-500 text-white',   inactive: 'bg-brand-50 text-brand-500' },
                  { key: 'RED',   label: `Breach (${preFilteredRooms.filter(r => r.sla_status === 'RED').length})`,  active: 'bg-red-500 text-white',     inactive: 'bg-red-50 text-red-600' },
                  { key: 'AMBER', label: `At Risk (${preFilteredRooms.filter(r => r.sla_status === 'AMBER').length})`,active: 'bg-amber-500 text-white',   inactive: 'bg-amber-50 text-amber-600' },
                  { key: 'GREEN', label: `Met (${preFilteredRooms.filter(r => r.sla_status === 'GREEN').length})`,   active: 'bg-emerald-500 text-white', inactive: 'bg-emerald-50 text-emerald-600' },
                ].map(({ key, label, active, inactive }) => (
                  <button key={key} onClick={() => setSlaFilter(key)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${slaFilter === key ? active : inactive}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)' }}>
                    <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Room Name</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-brand-400 uppercase tracking-wider">Class</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider w-40">Uptime</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-brand-400 uppercase tracking-wider">Downtime (mins)</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-brand-400 uppercase tracking-wider">Total Cases</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-brand-400 uppercase tracking-wider">Open Cases</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-50">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <svg className="animate-spin w-7 h-7 text-magenta-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                          <p className="text-gray-400 text-sm">Loading rooms…</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredRooms.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-gray-400 text-sm">
                        No rooms match the selected filters.
                      </td>
                    </tr>
                  ) : filteredRooms.map(room => (
                    <tr key={room.id}
                      className={`hover:bg-brand-50/40 transition-colors border-l-2 ${
                        room.sla_status === 'RED' ? 'border-l-red-400' :
                        room.sla_status === 'AMBER' ? 'border-l-amber-400' : 'border-l-transparent'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{room.room_name}</p>
                        <p className="text-xs text-gray-400">{room.room_id} · {room.room_type}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                          room.sla_class === 'A' ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-500'
                        }`}>
                          Class {room.sla_class}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-brand-700 text-sm">{room.location_name}</p>
                        {room.building_name && <p className="text-xs text-gray-400">{room.building_name}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className={`font-semibold ${room.sla_met ? 'text-emerald-600' : 'text-red-600'}`}>{uptime4(room.uptime_pct)}</span>
                            <span className="text-gray-400">{room.target_uptime_pct}%</span>
                          </div>
                          <UptimeBar actual={room.uptime_pct} target={room.target_uptime_pct} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-700 font-medium">
                        {room.downtime_minutes > 0 ? Math.round(room.downtime_minutes) : <span className="text-gray-300">0</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {room.total_cases > 0
                          ? <span className="text-gray-700 font-semibold text-sm">{room.total_cases}</span>
                          : <span className="text-gray-300 text-sm">0</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {room.open_cases > 0
                          ? <span className="bg-magenta-100 text-magenta-600 font-bold text-xs px-2 py-0.5 rounded-full">{room.open_cases}</span>
                          : <span className="text-gray-300 text-xs">0</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filteredRooms.length > 0 && (
                  <tfoot>
                    <tr className="bg-brand-50/50 border-t-2 border-brand-200">
                      <td colSpan={4} className="px-4 py-3 text-xs text-gray-400 text-right">
                        {filteredRooms.length} room{filteredRooms.length !== 1 ? 's' : ''} · total downtime
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-brand-600 text-sm">
                        {Math.round(filteredRooms.reduce((s, r) => s + (r.downtime_minutes || 0), 0)).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-brand-600 text-sm">
                        {filteredRooms.reduce((s, r) => s + (r.total_cases || 0), 0)}
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-magenta-600 text-sm">
                        {filteredRooms.reduce((s, r) => s + (r.open_cases || 0), 0)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

        </>
      )}
    </div>
  );
}
